import { Component, type ErrorInfo, type ReactNode } from 'react';

import { AlertTriangle, RefreshCw } from 'lucide-react';

import { GhostButton, PrimaryButton } from '@/lib/ui/atoms';
import { GlassCard } from '@/lib/ui/layouts/GlassCard';

// ── ErrorFallback ─────────────────────────────────────────────────────────────

interface ErrorFallbackProps {
  error: Error;
  reset: () => void;
}

export function ErrorFallback({ error, reset }: ErrorFallbackProps) {
  return (
    <div className="flex min-h-[50vh] items-center justify-center p-6">
      <GlassCard className="w-full max-w-md space-y-4 text-center">
        <div className="flex justify-center">
          <AlertTriangle className="size-12 text-destructive" aria-hidden="true" />
        </div>
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Something went wrong</h2>
          <p className="text-sm text-muted-foreground">
            An unexpected error occurred. You can try again or go back to the home page.
          </p>
        </div>
        {import.meta.env.DEV && (
          <pre className="max-h-32 overflow-auto rounded bg-muted p-3 text-left text-xs text-muted-foreground">
            {error.message}
          </pre>
        )}
        <div className="flex justify-center gap-2">
          <GhostButton size="sm" onClick={reset}>
            <RefreshCw className="mr-1.5 size-3.5" aria-hidden="true" />
            Try again
          </GhostButton>
          <PrimaryButton
            size="sm"
            onClick={() => {
              window.location.href = '/';
            }}
          >
            Go home
          </PrimaryButton>
        </div>
      </GlassCard>
    </div>
  );
}

// ── ErrorBoundary ─────────────────────────────────────────────────────────────

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Provide a custom fallback UI. Receives no props — use ErrorFallback directly if you need reset access. */
  fallback?: ReactNode;
  /** Called whenever an error is caught. Use for logging (e.g. Sentry). */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error) {
      return fallback ?? <ErrorFallback error={error} reset={this.reset} />;
    }

    return children;
  }
}
